/**
 * TypeScript mirror of the backend durable evidence-lineage read contract.
 *
 * Source of truth: `backend/app/modules/evidence/schemas/atom.py`
 * (`EvidenceAtomRead`, `EvidenceAtomEdgeRead`, `EvidenceLineage`).
 *
 * These are the shapes returned by the lineage endpoints
 * (`GET /api/v3/evidence/atoms/{atom_id}` and `/lineage`) that the §17
 * lineage-expansion UI renders. Hand-rolled boundary validation follows the
 * `citationArtifact.ts` convention (the repo does not use Zod in services).
 */

import type { SourceKind, SourceRef } from "./citationArtifact";

export type EdgeRelation = "input_to" | "derived_from" | "supersedes";
export type LocatorStatus = "verified" | "unverified";

export interface EvidenceAtom {
	atom_id: string;
	ref_kind: SourceKind;
	/** The locator union (validated backend-side); parsed to a SourceRef in the UI. */
	source_ref: SourceRef;
	canonical_locator: string;
	content_hash: string;
	locator_status: LocatorStatus;
	verifier_version?: string | null;
	created_by_run_id?: string | null;
	created_at?: string | null;
	updated_at?: string | null;
	edges?: EvidenceAtomEdge[] | null;
}

export interface EvidenceAtomEdge {
	parent_atom_id: string;
	child_atom_id: string;
	relation: EdgeRelation;
	created_by_run_id?: string | null;
}

export interface EvidenceLineage {
	root_atom_id: string;
	nodes: EvidenceAtom[];
	edges: EvidenceAtomEdge[];
	truncated: boolean;
}

// ---------------------------------------------------------------------------
// Boundary validation (dependency-free — matches citationArtifact.ts style)
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Structurally validate a lineage payload. Rejects a malformed response at the
 * boundary; does not deeply re-validate each nested source_ref (the backend
 * discriminated union is authoritative). Returns a normalized `EvidenceLineage`
 * or `null` on mismatch (services degrade to the in-payload derivation tree).
 */
export function parseEvidenceLineage(input: unknown): EvidenceLineage | null {
	if (!isRecord(input)) return null;
	if (typeof input.root_atom_id !== "string") return null;
	if (!Array.isArray(input.nodes) || !Array.isArray(input.edges)) return null;

	const nodes: EvidenceAtom[] = [];
	for (const node of input.nodes) {
		if (!isRecord(node) || typeof node.atom_id !== "string") return null;
		if (!isRecord(node.source_ref)) return null;
		nodes.push(node as unknown as EvidenceAtom);
	}

	const edges: EvidenceAtomEdge[] = [];
	for (const edge of input.edges) {
		if (
			!isRecord(edge) ||
			typeof edge.parent_atom_id !== "string" ||
			typeof edge.child_atom_id !== "string" ||
			typeof edge.relation !== "string"
		) {
			return null;
		}
		edges.push(edge as unknown as EvidenceAtomEdge);
	}

	return {
		root_atom_id: input.root_atom_id,
		nodes,
		edges,
		truncated: input.truncated === true,
	};
}
